import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SubmitConsent } from '../modules/consent/interfaces/consent';
import {
  AddReferralRequest,
  ReferralDetails,
  ReferralInfo,
} from '../modules/referrals/interfaces/referrals.model';
import { RequestForReevaluationSubmitRequest } from '../modules/referrals/models/request-reevaluation/request-reevaluation-submit.model';
import { ReviewRequestForReevaluationSubmitRequest } from '../modules/referrals/models/review-request-reevaluation/review-request-reevaluation-submit.model';
import { ApiResponse } from '../shared/interfaces/api-response';
import { IAppConfig } from '../shared/interfaces/app-config';
import { IMeetingDetails } from '../shared/interfaces/meeting-details';
import { AttendeeList } from '../shared/interfaces/meetingAttendee';
import { StaffInfo } from '../shared/interfaces/staff-info';
import { ReferralWithdrawInput } from '../shared/interfaces/withdrawal-input-obj';
import { AppConfigService } from './app-config.service';
import { assertTrustedUrl } from '../shared/functions/validate-url';

@Injectable({
  providedIn: 'root',
})
export class ReferralsService {
  config: IAppConfig;

  httpOptions: { headers: HttpHeaders } = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      accept: '*/*',
    }),
  };

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService
  ) {
    this.config = this.appConfigService.getConfig();
  }

 
  // TODO: This method should return `ReferralInfo` but currently getting errors. Please fix it later.
  public GetReferral(referralSk?: string): Observable<ReferralInfo> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Referral/${encodeURIComponent(referralSk)}`,
      this.config.apiUrl
    );
    return this.http
      .get<ReferralInfo>(url, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  public AddorUpdateReferral(
    addInitialReferral:
      | AddReferralRequest
      | RequestForReevaluationSubmitRequest
      | ReviewRequestForReevaluationSubmitRequest,
    referralSk?: string
  ): Observable<ApiResponse<ReferralInfo>> {
    if (referralSk === undefined || referralSk === null || referralSk === '') {
      return this.http
        .post<ApiResponse<ReferralInfo>>(
          `${this.config.apiUrl}/Referral`,
          addInitialReferral,
          this.httpOptions
        )
        .pipe(catchError(this.handleError));
    } else {
      return this.http
        .put<ApiResponse<ReferralInfo>>(
          assertTrustedUrl(`${this.config.apiUrl}/Referral/${encodeURIComponent(referralSk)}`, this.config.apiUrl),
          addInitialReferral,
          this.httpOptions
        )
        .pipe(catchError(this.handleError));
    }
  }

  public UpdateReferralStatus(
    referralSk?: string,
    referralStatus?: number,
    personSk?: string
  ): Observable<number> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Referral/${encodeURIComponent(referralSk)}/${encodeURIComponent(String(referralStatus))}/${encodeURIComponent(personSk)}`,
      this.config.apiUrl
    );
    return this.http
      .put<number>(url, null, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  public SubmitConsent(
    requestPayload: SubmitConsent | ReferralWithdrawInput
  ): Observable<ApiResponse<boolean>> {
    return this.http
      .post<ApiResponse<boolean>>(
        `${this.config.apiUrl}/Pwn/SubmitConsent`,
        JSON.stringify(requestPayload),
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  getAttendees(meetingType: number, locId: string): Observable<StaffInfo> {
    return this.http
      .get<StaffInfo>(
        `${this.config.studentApiUrl}/Meeting/GetAttendees/?meetingtype=${meetingType}&locId=${locId}`
      )
      .pipe(catchError(this.handleError));
  }

  getAttendeesList(referralSK: string, meetingType: string): Observable<AttendeeList> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/Meeting/GetMeetingAttendees/${encodeURIComponent(referralSK)}/${encodeURIComponent(meetingType)}`,
      this.config.apiUrl
    );
    return this.http
      .get<AttendeeList>(url)
      .pipe(catchError(this.handleError));
  }

  scheduleMeeting(
    payload: IMeetingDetails
  ): Observable<ApiResponse<IMeetingDetails>> {
    return this.http
      .post<ApiResponse<IMeetingDetails>>(
        `${this.config.studentApiUrl}/Meeting/Add`,
        payload
      )
      .pipe(catchError(this.handleError));
  }

  requestRevalStatusById(osisId: string): Observable<ReferralDetails> {
    const url = assertTrustedUrl(
      `${this.config.authApiUrl}/referral/GetReferralByNYCID/${encodeURIComponent(osisId)}`,
      this.config.authApiUrl
    );
    return this.http
      .get<ReferralDetails>(url)
      .pipe(catchError(this.handleError));
  }

  public getUploadedDate(studentSk: string): Observable<Date> {
    return this.http
      .get<Date>(
        `${this.config.apiUrl}/referral/uploadedDate?studentsk=${studentSk}`
      )
      .pipe(catchError(this.handleError));
  }

  // Error handling
  handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => {
      return errorMessage;
    });
  }
}
